import {
    RiArrowRightSLine,
    RiLockPasswordLine,
    RiUserLine,
} from '@remixicon/react';
import { ComponentProps, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoWithText } from '~/components/logo';
import LoginMicrosoft from '~/components/login/microsoft';
import { cn } from '~/helpers/tw';
import { INSTANCE_ID } from '~/constants';
import { usePostHog } from '@posthog/react';
import { getClientLogoImageWithText } from '~/helpers/image';
import { Toast } from '../toast';
import LoginTwoFactor from './two-factor';

interface FormErrors {
    invalidUser: boolean;
    invalidPassword: boolean;
}

interface TwoFactorAuth {
    active: boolean;
    email: boolean;
    sms: boolean;
}

interface FetchResult {
    error: number;
    title: string;
    message: string;
    userID: number;
    user: Object;
    twoFactorAuth?: TwoFactorAuth;
    remainingAttempts?: number;
    blockingUserDate?: string;
    type?: ComponentProps<typeof Toast>['type'];
}

type LoginStandardProps = {
    twoFactorAuthActive: boolean;
    setTwoFactorAuthActive: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function LoginStandard({
    twoFactorAuthActive,
    setTwoFactorAuthActive,
}: LoginStandardProps) {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const isClientLogo = [137, 143, 350, 184, 232].includes(INSTANCE_ID);

    const [user, setUser] = useState('');
    const [password, setPassword] = useState('');
    const [fetchResult, setFetchResult] = useState<FetchResult>({
        error: 0,
        title: '',
        message: '',
        userID: 0,
        user: {},
    });
    const [formSubmitResult, setFormSubmitResult] = useState<FormErrors>({
        invalidUser: false,
        invalidPassword: false,
    });
    const [loading, setLoading] = useState(false);
    const [openErrorToast, setOpenErrorToast] = useState<boolean>(false);
    const onToastClose = () => setOpenErrorToast(false);

    useEffect(() => {
        // se estiver fechado nao corre o effect
        if (!openErrorToast) return undefined;
        // se estiver aberto corre um timeout e fecha o toast passado 6s
        const timeoutId = setTimeout(() => setOpenErrorToast(false), 6000);
        return () => clearTimeout(timeoutId);
    }, [openErrorToast]);

    useEffect(() => {
        if (fetchResult.error < 0 && fetchResult?.remainingAttempts == null) {
            setOpenErrorToast(true);
        } else if (fetchResult.twoFactorAuth?.active) {
            sessionStorage.setItem(
                '2fa_pending',
                JSON.stringify({
                    userID: fetchResult.userID,
                    user: fetchResult.user,
                    twoFactorAuth: fetchResult.twoFactorAuth,
                })
            );
            setTwoFactorAuthActive(true);
        }
    }, [fetchResult]);

    const promiseSignInUser = async (
        bodyParams: URLSearchParams
    ): Promise<FetchResult> => {
        try {
            const response = await fetch('/LoginV2/index.server.php', {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded;charset=UTF-8',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: bodyParams.toString(),
                credentials: 'include',
            });

            const data = await response.json();

            if (data.error < 0 && !('tentativas_restantes' in data)) {
                return {
                    error: data.error,
                    title: data.title,
                    type: data.type,
                    message: data.messages[0]?.message || t('error.anErrorOccurred'),
                    userID: 0,
                    user: {},
                };
            }

            const result: FetchResult = {
                error: data.error,
                title: data.title,
                type: data.type,
                message: data.message,
                userID: data.id_utilizador,
                user: data.user || {},
            };

            if ('tentativas_restantes' in data) {
                result.remainingAttempts = data.tentativas_restantes;
            }

            if ('data_bloq_temp' in data) {
                result.blockingUserDate = data.data_bloq_temp;
            }

            if (data['2fa']) {
                result.twoFactorAuth = {
                    active: data['2fa'].ativo,
                    email: data['2fa'].email,
                    sms: data['2fa'].sms,
                };
            }

            return result;
        } catch (error) {
            console.log('Erro em promiseSignInUser:', error);
            return {
                error: -1,
                title: t('auth.authentication'),
                type: 'error',
                message: t('error.anErrorOccurred'),
                userID: 0,
                user: {},
            };
        }
    };

    const handleSubmit = async () => {
        if (loading) {
            return;
        }
        setLoading(true);
        const formResult = {} as FormErrors;
        formResult.invalidUser = false;
        formResult.invalidPassword = false;

        if (typeof user !== 'string' || user.length === 0) {
            formResult.invalidUser = true;
        }

        if (typeof password !== 'string' || password.length === 0) {
            formResult.invalidPassword = true;
        }

        if (formResult.invalidUser || formResult.invalidPassword) {
            setFormSubmitResult(formResult);
            setLoading(false);
            return;
        }

        const bodyParams = new URLSearchParams();
        bodyParams.append('action', 'login');
        bodyParams.append('post_data[username]', user.toString());
        bodyParams.append('post_data[password]', password.toString());

        // call legacy server to validate login

        const result: FetchResult = await promiseSignInUser(bodyParams);
        setLoading(false);

        setFormSubmitResult(formResult);
        setFetchResult(result);

        // Redirect after 2FA success
        if (
            result.error === 0 &&
            (!result.twoFactorAuth || !result.twoFactorAuth.active)
        ) {
            posthog?.capture('login_success', { method: 'standard' });
            setOpenErrorToast(false);

            if (typeof window !== 'undefined') {
                setTimeout(() => {
                    window.location.href = '/Home';
                }, 50);
            }
        } else if (result.error !== 0 && !result.twoFactorAuth?.active) {
            posthog?.capture('login_failed', {
                method: 'standard',
                remaining_attempts: result.remainingAttempts,
            });
        }
    };

    const [scheduleInfo, setScheduleInfo] = useState<FetchResult | null>(null);

    const promiseUpdateSchedule = async (
        bodyParams: URLSearchParams
    ): Promise<FetchResult> => {
        try {
            const response = await fetch('/LoginV2/index.server.php', {
                method: 'POST',
                headers: {
                    'Content-Type':
                        'application/x-www-form-urlencoded;charset=UTF-8',
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: bodyParams.toString(),
                credentials: 'include',
            });

            const data = await response.json();
            if (data.error < 0) {
                return {
                    error: data.error,
                    type: data.type,
                    title: data.title,
                    message:
                        data.messages[0].message !== ''
                            ? data.messages[0].message
                            : t('error.anErrorOccurred'),
                    userID: 0,
                    user: {},
                };
            }

            const result = {} as FetchResult;
            result.error = data.error;
            result.title = data.title;
            result.type = data.type;
            result.message = data.message;

            return result;
        } catch (error) {
            console.log('Erro em promiseUpdateSchedule:', error);
            return {
                error: -1,
                title: t('auth.authentication'),
                type: 'error',
                message: t('error.anErrorOccurred'),
                userID: 0,
                user: {},
            };
        }
    };

    const updateSchedule = async () => {
        const bodyParams = new URLSearchParams();
        bodyParams.append('action', 'updateSchedule');

        try {
            const result: FetchResult = await promiseUpdateSchedule(bodyParams);

            // If it's an informational message about maintenance, save it in a separate state
            if (result.type === 'info' && result.message) {
                setScheduleInfo(result);
            }
        } catch (error) {
            console.warn('updateSchedule falhou:', error);
        }
    };

    useEffect(() => {
        updateSchedule();
    }, []);

    useEffect(() => {
        const saved = sessionStorage.getItem('2fa_pending');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setFetchResult((prev) => ({
                    ...prev,
                    userID: parsed.userID,
                    user: parsed.user,
                    twoFactorAuth: parsed.twoFactorAuth,
                }));
                setTwoFactorAuthActive(true);
            } catch {
                sessionStorage.removeItem('2fa_pending');
            }
        }
    }, [setFetchResult, setTwoFactorAuthActive]);

    return (
        <div className="relative flex h-full w-full items-center justify-center">
            {twoFactorAuthActive === true ? (
                <LoginTwoFactor
                    userID={fetchResult.userID}
                    email={fetchResult.twoFactorAuth?.email}
                    sms={fetchResult.twoFactorAuth?.sms}
                    user={fetchResult.user}
                    goBackCallback={() => {
                        sessionStorage.removeItem('2fa_pending');
                        setTwoFactorAuthActive(false);
                    }}
                    onSuccessCallback={() => {
                        posthog?.capture('login_success', { method: '2fa' });
                        sessionStorage.removeItem('2fa_pending');
                        setOpenErrorToast(false);
                        if (typeof window !== 'undefined') {
                            setTimeout(() => {
                                window.location.href = '/Home';
                            }, 50);
                        }
                    }}
                />
            ) : (
                <div className="min-[992px]:w-[58%] min-[992px]:max-w-none relative w-full">
                    {/* Toast posicionado fora do card, acima do form (fora do fluxo) */}
                    {(scheduleInfo || openErrorToast) && (
                        <div className="absolute bottom-full left-1/2 z-50 mb-0 w-full -translate-x-1/2 -translate-y-4">
                            {scheduleInfo && (
                                <div className="mb-2">
                                    <Toast
                                        className="w-full"
                                        type="info"
                                        title={scheduleInfo.title}
                                        message={scheduleInfo.message}
                                        close={() => setScheduleInfo(null)}
                                    />
                                </div>
                            )}

                            {openErrorToast && (
                                <div>
                                    <Toast
                                        className="w-full"
                                        type={fetchResult.type}
                                        title={fetchResult.title}
                                        message={fetchResult.message}
                                        close={onToastClose}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="w-full rounded-md border-gray-50 bg-white shadow">
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                        >
                            <div className="p-4">
                                {isClientLogo ? (
                                    <>
                                        <img
                                            src={getClientLogoImageWithText(
                                                INSTANCE_ID
                                            )}
                                            alt=""
                                            className="h-11 w-auto"
                                        />
                                    </>
                                ) : (
                                    <LogoWithText className="h-8 w-auto" />
                                )}
                            </div>

                            <h1 className="px-4 pb-4 text-lg font-bold leading-tight tracking-tight text-gray-900">
                                {t('auth.welcome')}!
                            </h1>

                            <div className="px-4 pb-1">
                                <label
                                    htmlFor="user"
                                    className="text-sm text-gray-900"
                                >
                                    {t('common.user')}
                                </label>
                                <div className="flex">
                                    <span
                                        className={cn(
                                            'rounded-e-0 inline-flex items-center rounded-s-md border border-e-0 px-3 text-sm',
                                            {
                                                'border-red-600 bg-red-50':
                                                    formSubmitResult?.invalidUser,
                                                'border-gray-300':
                                                    !formSubmitResult?.invalidUser,
                                            }
                                        )}
                                    >
                                        <RiUserLine
                                            className={cn('p-0.5', {
                                                'fill-red-700':
                                                    formSubmitResult?.invalidUser,
                                                'fill-gray-700':
                                                    !formSubmitResult?.invalidUser,
                                            })}
                                        />
                                    </span>
                                    <input
                                        type="text"
                                        name="user"
                                        id="user"
                                        autoComplete="username"
                                        onKeyDown={(e) =>
                                            e.key === 'Enter'
                                                ? handleSubmit()
                                                : null
                                        }
                                        onChange={(el) =>
                                            setUser(el.target.value)
                                        }
                                        className={cn(
                                            'input-primary block w-full rounded-e-md p-2.5 text-gray-900',
                                            {
                                                'border border-red-600 bg-red-50 focus:border-red-600':
                                                    formSubmitResult?.invalidUser,
                                                'border border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500':
                                                    !formSubmitResult?.invalidUser,
                                            }
                                        )}
                                        placeholder={t('common.user')}
                                    />
                                </div>
                                <div
                                    className={cn(
                                        'mt-1 w-full text-sm text-red-700',
                                        formSubmitResult?.invalidUser
                                            ? 'visible'
                                            : 'invisible'
                                    )}
                                >
                                    {t('auth.fillUsername')}
                                </div>
                            </div>

                            <div className="px-4">
                                <label
                                    htmlFor="password"
                                    className="text-sm text-gray-900"
                                >
                                    {t('auth.password')}
                                </label>
                                <div className="flex">
                                    <span
                                        className={cn(
                                            'rounded-e-0 inline-flex items-center rounded-s-md border border-e-0 px-3 text-sm',
                                            {
                                                'border-red-600 bg-red-50':
                                                    formSubmitResult?.invalidPassword ||
                                                    fetchResult?.remainingAttempts !=
                                                        null,
                                                'border-gray-300': !(
                                                    formSubmitResult?.invalidPassword ||
                                                    fetchResult?.remainingAttempts !=
                                                        null
                                                ),
                                            }
                                        )}
                                    >
                                        <RiLockPasswordLine
                                            className={cn('p-0.5', {
                                                'fill-red-700':
                                                    formSubmitResult?.invalidPassword ||
                                                    fetchResult?.remainingAttempts !=
                                                        null,
                                                'fill-gray-700': !(
                                                    formSubmitResult?.invalidPassword ||
                                                    fetchResult?.remainingAttempts !=
                                                        null
                                                ),
                                            })}
                                        />
                                    </span>
                                    <input
                                        type="password"
                                        name="password"
                                        id="password"
                                        autoComplete="current-password"
                                        onKeyDown={(e) =>
                                            e.key === 'Enter'
                                                ? handleSubmit()
                                                : null
                                        }
                                        onChange={(el) =>
                                            setPassword(el.target.value)
                                        }
                                        className={cn(
                                            'input-primary block w-full rounded-e-md border p-2.5 text-gray-900',
                                            {
                                                'border-red-600 bg-red-50 focus:border-red-600':
                                                    formSubmitResult?.invalidPassword ||
                                                    fetchResult?.remainingAttempts !=
                                                        null,
                                                'border-gray-300 bg-white focus:border-blue-500 focus:ring-blue-500':
                                                    !(
                                                        formSubmitResult?.invalidPassword ||
                                                        fetchResult?.remainingAttempts !=
                                                            null
                                                    ),
                                            }
                                        )}
                                        placeholder={t('auth.password')}
                                    />
                                </div>
                                <div
                                    role="alert"
                                    className={cn(
                                        'mt-1 w-full text-sm text-red-700',
                                        formSubmitResult?.invalidPassword ||
                                            fetchResult?.remainingAttempts !=
                                                null
                                            ? 'block'
                                            : 'hidden'
                                    )}
                                >
                                    {formSubmitResult?.invalidPassword &&
                                        t('auth.fillPassword')}
                                    {!formSubmitResult?.invalidPassword &&
                                        fetchResult?.remainingAttempts ===
                                            0 && (
                                            <>
                                                {t('auth.blockedUntil')}
                                                {fetchResult.blockingUserDate}
                                            </>
                                        )}
                                    {!formSubmitResult?.invalidPassword &&
                                        fetchResult?.remainingAttempts !=
                                            null &&
                                        fetchResult.remainingAttempts > 0 && (
                                            <>
                                                {t(
                                                    'auth.attemptsRemaining'
                                                )}
                                                {fetchResult.remainingAttempts}
                                            </>
                                        )}
                                </div>
                            </div>

                            <div className="p-4">
                                <button
                                    type="button"
                                    disabled={loading}
                                    className={cn(
                                        'btn-sign-in-btn-gradient size-11 w-full rounded-md outline-transparent',
                                        {
                                            'cursor-not-allowed': loading,
                                        }
                                    )}
                                    onClick={handleSubmit}
                                >
                                    <div className="inline-flex items-center">
                                        {loading ? (
                                            <span className="loading loading-spinner loading-md"></span>
                                        ) : (
                                            <>
                                                <span className="font-bold">
                                                    {t('auth.enter')}
                                                </span>
                                                <RiArrowRightSLine />
                                            </>
                                        )}
                                    </div>
                                </button>

                                {isClientLogo ? (
                                    <div className="mt-6 flex justify-end">
                                        <LogoWithText className="h-5" />
                                    </div>
                                ) : (
                                    ''
                                )}
                            </div>
                        </form>

                        {twoFactorAuthActive === false && (
                            <div className="rounded-md">
                                <LoginMicrosoft />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
